import { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { getIntegrationLogs } from "helpers/destinations/createDestination";

import { getAllDestinationsRedux } from "../../../../../redux/slices/destinations/destinations.asyncActions";
import { getSingleTrackerRedux } from "../../../../../redux/slices/trackers/trackers.asyncActions";
import { getDestinationTabs, TAB_LABELS } from "../DestinationModal.config";
import {
    Destination,
    DestinationModalMode,
    IntegrationLog,
    TabType,
    Tracker,
} from "../DestinationModal.types";

interface UseDestinationModalOptions {
    destination: Destination;
    tracker?: Tracker;
    onClose?: () => void;
    onRefresh?: () => void;
    initialTab?: TabType | null;
    mode?: DestinationModalMode;
}

interface UseDestinationModalReturn {
    activeTab: TabType;
    isDeleteDialogOpen: boolean;
    availableTabs: TabType[];
    integrationLogs: IntegrationLog[];
    isLoadingLogs: boolean;

    setActiveTab: (tab: TabType) => void;
    openDeleteDialog: () => void;
    closeDeleteDialog: () => void;
    refreshTracker: () => void;
    handleClose: () => void;

    getTabLabel: (tab: TabType, language: any) => string;
    hasTab: (tab: TabType) => boolean;
}

export const useDestinationModal = ({
    destination,
    tracker,
    onClose,
    onRefresh,
    initialTab,
    mode = "tracker",
}: UseDestinationModalOptions): UseDestinationModalReturn => {
    const dispatch = useDispatch();
    const params = useParams<{ id: string }>();

    const availableTabs = useMemo(() => {
        return getDestinationTabs(destination?.destination_type?.code, mode);
    }, [destination?.destination_type?.code, mode]);

    // State
    const [activeTab, setActiveTab] = useState<TabType>(
        availableTabs[0] || "overview",
    );
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [integrationLogs, setIntegrationLogs] = useState<IntegrationLog[]>(
        [],
    );
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);

    const fetchIntegrationLogs = useCallback(async () => {
        if (destination?.id && activeTab === "reconnect") {
            setIsLoadingLogs(true);
            try {
                const response = await getIntegrationLogs(destination.id);
                setIntegrationLogs(response?.data?.integration_logs || []);
            } catch (error) {
                console.error("Error fetching integration logs:", error);
                setIntegrationLogs([]);
            } finally {
                setIsLoadingLogs(false);
            }
        }
    }, [destination?.id, activeTab]);

    useEffect(() => {
        fetchIntegrationLogs();
    }, [destination?.id, activeTab]);

    useEffect(() => {
        if (initialTab && availableTabs.includes(initialTab)) {
            setActiveTab(initialTab);
        } else if (
            destination?.status === 0 &&
            availableTabs.includes("reconnect")
        ) {
            setActiveTab("reconnect");
        } else {
            setActiveTab(availableTabs[0] || "overview");
        }
    }, [initialTab, destination?.id, destination?.status, availableTabs]);

    const openDeleteDialog = useCallback(() => {
        setIsDeleteDialogOpen(true);
    }, []);

    const closeDeleteDialog = useCallback(() => {
        setIsDeleteDialogOpen(false);
    }, []);

    const refreshTracker = useCallback(() => {
        fetchIntegrationLogs();
        if (onRefresh) {
            onRefresh();
        } else if (mode === "standalone") {
            dispatch(getAllDestinationsRedux() as any);
        } else if (params.id) {
            dispatch(getSingleTrackerRedux(params.id) as any);
        }
    }, [dispatch, params.id, mode, onRefresh, fetchIntegrationLogs]);

    const handleClose = useCallback(() => {
        setIsDeleteDialogOpen(false);
        setActiveTab(availableTabs[0] || "overview");
        onClose?.();
    }, [onClose, availableTabs]);

    const getTabLabel = useCallback((tab: TabType, language: any): string => {
        const tabConfig = TAB_LABELS[tab];
        if (!tabConfig) return tab;

        const keys = tabConfig.key.split(".");
        let value: any = language;
        for (const key of keys) {
            value = value?.[key];
            if (value === undefined) break;
        }

        return value || tabConfig.default;
    }, []);

    const hasTab = useCallback(
        (tab: TabType): boolean => {
            return availableTabs.includes(tab);
        },
        [availableTabs],
    );

    return {
        activeTab,
        isDeleteDialogOpen,
        availableTabs,
        integrationLogs,
        isLoadingLogs,

        setActiveTab,
        openDeleteDialog,
        closeDeleteDialog,
        refreshTracker,
        handleClose,

        getTabLabel,
        hasTab,
    };
};

export default useDestinationModal;
